import { LoadSpinner } from '@calizahq/react-components-v2'
import React, { useCallback, useMemo } from 'react'

import { NoResults, Table } from '@/components'
import { useFiltersContext, useUserContext } from '@/context'
import { NotificationsMutations } from '@/graphql/mutations/notifications.mutations'
import { NotificationsQueries } from '@/graphql/queries/notifications.queries'
import { NotifiableType } from '@/graphql/subscriptions/notifications.subscriptions'
import { useMobile } from '@/hooks'
import { useGraphql, useGraphqlMutation } from '@/hooks/useGraphql'

import { StatusFilter } from './Filters/StatusFilter'
import { MobileNotifications } from './MobileNotifications'
import { NotificationsColumns } from './NotificationAlerts.data'

export const NotificationAlerts = () => {
  const [{ notificationsFilters }] = useFiltersContext()
  const { status } = notificationsFilters
  const { isMobile } = useMobile()
  const { user } = useUserContext()
  const {
    data: notificationsData,
    refetch,
    isLoading,
  } = useGraphql<NotificationsQueries.GET.Response>(
    NotificationsQueries.QUERY_KEY,
    NotificationsQueries.getNotificationAlerts(),
    {
      userId: user?.id,
      viewed: status ? false : null,
    },
    {
      enabled: !!user?.id,
      refetchOnMount: 'always',
    },
  )

  const { refetch: refetchTopLevelCache } = useGraphql(
    NotificationsQueries.QUERY_KEY,
    NotificationsQueries.getNotificationAlerts(),
    {
      userId: user?.id,
      viewed: false,
    },
    {
      enabled: !!user?.id,
    },
  )

  const { mutate } = useGraphqlMutation(NotificationsMutations.markNotificationAsViewed(), {
    onSuccess: () => {
      refetch()
      refetchTopLevelCache()
    },
  })

  const data = useMemo(() => {
    return notificationsData && notificationsData.notificationAlerts ? notificationsData.notificationAlerts : []
  }, [notificationsData])

  const handleMarkNotification = useCallback(
    (notifiableId: number, notifiableType: NotifiableType) => {
      if (user?.id && notifiableId && notifiableType) {
        mutate({
          notificationInfo: {
            notifiableId,
            notifiableType,
            userId: user?.id,
          },
        })
      }
    },
    [mutate, user?.id],
  )

  const columns = useMemo(() => {
    return NotificationsColumns(handleMarkNotification)
  }, [handleMarkNotification])
  return (
    <div className="flex w-full flex-col p-4 md:p-8">
      <div className="mb-2 flex w-full flex-row flex-wrap items-center">
        <h2 className="mr-4 flex-1 text-header-sm md:mb-0 md:text-header-lg">Notifications</h2>
        <div className="flex flex-col md:hidden">
          <StatusFilter />
        </div>
      </div>
      <div className="hidden flex-col items-center pb-6 md:flex md:flex-row-reverse md:justify-end lg:flex-row">
        <StatusFilter />
      </div>

      {isLoading ? (
        <div className="flex h-20 w-full items-center justify-center">
          <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
        </div>
      ) : (
        <>
          {isMobile ? (
            data && data?.length > 0 ? (
              data?.map((notification, i) => (
                <MobileNotifications
                  handleMarkNotification={handleMarkNotification}
                  key={`${notification?.id}${i}`}
                  notification={notification}
                />
              ))
            ) : (
              <NoResults />
            )
          ) : (
            <Table className={''} columns={columns as any} data={data} />
          )}
        </>
      )}
    </div>
  )
}
